// app/src/Billing.jsx — subscription screen (Owner).
//
// Two tiers: Free (English only) and Multilingual ($5/mo · $50/yr), billed per
// workplace via Finix. The card form (Finix.js) tokenizes client-side; the token
// is sent to POST /workplaces/{wid}/subscription. Active subscribers can toggle
// auto-renew, update the card, or cancel (access stays until period end).

// ── Apple Pay ────────────────────────────────────────────────────────────────
// The <apple-pay-button> custom element is registered by Apple's SDK (loaded from
// applepay.cdn-apple.com in index.html). The element name is fixed by Apple.
//
// Apple's button `locale` wants a full BCP-47 tag; our UI language codes are bare
// ('es', 'zh'). Map them, and fall back to en-US for anything Apple doesn't ship.
const APPLE_PAY_LOCALES = {
  en: 'en-US', es: 'es-ES', th: 'th-TH', zh: 'zh-CN', vi: 'vi-VN',
  fr: 'fr-FR', ar: 'ar-AB', it: 'it-IT', ja: 'ja-JP', ko: 'ko-KR', el: 'el-GR',
};

/**
 * Can we show the button at all? Four gates, all required:
 *   1. a config flag — Apple Pay is OFF (see the commented-out SDK tag in
 *      index.html for why: Finix off-session recurring on an APPLE_PAY
 *      instrument is unconfirmed, and the domain registration is unresolved)
 *   2. the SDK actually loaded and REGISTERED the custom element. Checked
 *      separately from ApplePaySession because Safari defines ApplePaySession
 *      NATIVELY — so with the SDK tag removed, gates 1/3/4 all pass on iOS and
 *      we'd render an unupgraded <apple-pay-button> with no shadow root.
 *   3. ApplePaySession exists. Not a browser check: Apple's own SDK shims it
 *      onto Chrome. Kept only so gate 4 can't throw on a missing global.
 *   4. Apple says this device can pay. canMakePayments() THROWS on unsupported
 *      browsers rather than returning false, hence the try/catch + === true.
 *      Apple's HIG requires hiding the button here, not disabling it.
 */
function applePayAvailable() {
  if (!(window.__CREW_CONFIG__ || {}).applePayEnabled) return false;
  if (!window.customElements || !window.customElements.get('apple-pay-button')) return false;
  if (!window.ApplePaySession) return false;
  try { return window.ApplePaySession.canMakePayments() === true; } catch { return false; }
}

/**
 * @param {{ lang:string, onClick:function, type?:string, buttonstyle?:string }} props
 * `type`/`locale` are required by Apple; `buttonstyle` is optional. React passes
 * string props on dashed tags straight through as attributes, which is what the
 * element reads. Sizing is via Apple's CSS custom properties — set inline so we
 * don't need a global <style> block (React supports custom properties in style).
 */
function ApplePayButton({ lang, onClick, type = 'subscribe', buttonstyle = 'black' }) {
  return (
    <apple-pay-button
      buttonstyle={buttonstyle}
      type={type}
      locale={APPLE_PAY_LOCALES[lang] || 'en-US'}
      onClick={onClick}
      style={{
        '--apple-pay-button-width': '100%',
        '--apple-pay-button-height': '44px',
        '--apple-pay-button-border-radius': 'var(--radius-sm)',
        '--apple-pay-button-padding': '0px 0px',
        '--apple-pay-button-box-sizing': 'border-box',
        display: 'block', cursor: 'pointer',
      }}
    />
  );
}

function PlanCard({ title, price, highlight, children, action, onAction }) {
  return (
    <div style={{
      background: 'var(--surface)',
      border: highlight ? '2px solid var(--primary)' : '1px solid var(--line)',
      borderRadius: 'var(--radius)', padding: 18, marginBottom: 12,
    }}>
      <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 18, color: 'var(--ink)' }}>{title}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--primary)', margin: '6px 0 4px' }}>{price}</div>
      <div style={{ fontSize: 13, color: 'var(--sub)', marginBottom: action ? 14 : 0 }}>{children}</div>
      {action && (
        <button onClick={onAction} style={{
          width: '100%', padding: '11px 16px', cursor: 'pointer', borderRadius: 'var(--radius-sm)', border: 'none',
          background: 'var(--primary)', color: 'var(--primary-ink)', fontWeight: 700, fontSize: 14,
        }}>{action}</button>
      )}
    </div>
  );
}

const fmtUSD = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

function CardModal({ wid, cycle, email, mode = 'create', onClose, onDone }) {
  const formRef = React.useRef(null);
  const [paying, setPaying] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [lang] = window.useLang();

  // Evaluated once per mount: canMakePayments() doesn't change mid-session, and
  // re-checking on every render would churn the custom element.
  const [applePay] = React.useState(applePayAvailable);

  // TODO(apple-pay): clicking must open an ApplePaySession, which needs three
  // things that do not exist yet:
  //   1. Apple domain registration for crew.getkanjo.com (Finix dashboard —
  //      unconfirmed whether Finix registers the subdomain or only the apex)
  //   2. a server endpoint that calls Finix's create-merchant-session API and
  //      returns the opaque session to onvalidatemerchant
  //   3. a subscription-create path that accepts an Apple Pay token instead of a
  //      Finix card token (subscription-create/index.mjs takes `token` from Finix.js)
  // Until then applePayEnabled stays false and this never renders.
  const startApplePay = () => {
    console.warn('[billing] Apple Pay clicked but no merchant-session endpoint exists yet');
    setErr(t('billing.applePayUnavailable', 'Apple Pay isn’t set up yet — please use a card.'));
  };

  // Billing address (for WA sales tax). Only WA is taxed today.
  const [stateField, setStateField] = React.useState('');
  const [street, setStreet] = React.useState('');
  const [city, setCity] = React.useState('');
  const [zip, setZip] = React.useState('');
  const [tax, setTax] = React.useState(null);     // last lookup result
  const [taxBusy, setTaxBusy] = React.useState(false);

  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        const f = await window.crew.billing.finixForm.mountCardForm('crew-card-fields', { onUpdate: () => {} });
        if (live) formRef.current = f; else f.unmount();
      } catch (e) { setErr('Could not load the card form.'); }
    })();
    return () => { live = false; if (formRef.current) { try { formRef.current.unmount(); } catch {} } };
  }, []);

  // Preview the WA rate when a WA address is complete enough to resolve (debounced).
  const isWA = stateField.trim().toUpperCase() === 'WA';
  React.useEffect(() => {
    if (mode !== 'create') return;
    if (!isWA) { setTax(null); return; }
    if (!street.trim() || !zip.trim()) { setTax(null); return; }
    let live = true;
    setTaxBusy(true);
    const h = setTimeout(async () => {
      try {
        const r = await window.crew.billing.subscription.getTaxRate(wid, { state: 'WA', street, city, zip });
        if (live) setTax(r);
      } catch { if (live) setTax(null); }
      finally { if (live) setTaxBusy(false); }
    }, 500);
    return () => { live = false; clearTimeout(h); };
  }, [isWA, street, city, zip, wid, mode]);

  const baseCents = cycle === 'annual' ? 5000 : 500;
  const rate = (tax && tax.taxable && Number(tax.combinedRate)) || 0;
  const taxCents = Math.round(baseCents * rate);
  const totalCents = baseCents + taxCents;
  const waUnresolved = isWA && street.trim() && zip.trim() && tax && tax.taxable && tax.resolved === false;

  const pay = async () => {
    if (!formRef.current || paying) return;
    setPaying(true); setErr(null);
    try {
      const token = await formRef.current.tokenize();
      if (mode === 'update') {
        await window.crew.billing.subscription.updateCard(wid, token);
      } else {
        const serviceLocation = stateField.trim()
          ? { state: stateField.trim().toUpperCase(), street: street.trim(), city: city.trim(), zip: zip.trim() }
          : undefined;
        await window.crew.billing.subscription.createSubscription(wid, { token, cycle, email, serviceLocation });
      }
      onDone();
    } catch (e) {
      setErr((e && (e.body && e.body.message)) || (e && e.message) || 'Payment failed');
      setPaying(false);
    }
  };

  const inp = { width: '100%', padding: '10px 11px', marginTop: 5, marginBottom: 10, borderRadius: 'var(--radius-sm)', border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 14, boxSizing: 'border-box' };
  const lbl = { fontSize: 11.5, fontWeight: 700, color: 'var(--sub)' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,12,8,.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 430, background: 'var(--surface)', borderTopLeftRadius: 22, borderTopRightRadius: 22, padding: 20, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 18, color: 'var(--ink)', marginBottom: 4 }}>
          {t('billing.title', 'Multilingual')} · {cycle === 'annual' ? t('billing.annual', '$50 / year') : t('billing.monthly', '$5 / month')}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--sub)', marginBottom: 14 }}>{t('billing.cardHint', 'Enter your card to activate translation for your whole team.')}</div>

        {mode === 'create' && (
          <React.Fragment>
            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 }}>{t('billing.billingAddress', 'Billing address')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><div style={lbl}>{t('billing.state', 'State')}</div><input value={stateField} onChange={(e) => setStateField(e.target.value)} placeholder="WA" maxLength={2} style={{ ...inp, textTransform: 'uppercase' }} /></div>
              <div style={{ flex: 2 }}><div style={lbl}>{t('billing.zip', 'ZIP')}</div><input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="98101" style={inp} /></div>
            </div>
            {isWA && (
              <React.Fragment>
                <div style={lbl}>{t('billing.street', 'Street')}</div><input value={street} onChange={(e) => setStreet(e.target.value)} placeholder="123 Main St" style={inp} />
                <div style={lbl}>{t('billing.city', 'City')}</div><input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Seattle" style={inp} />
              </React.Fragment>
            )}
          </React.Fragment>
        )}

        {mode === 'create' && applePay && (
          <React.Fragment>
            <ApplePayButton lang={lang} onClick={startApplePay} type="subscribe" buttonstyle="black" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0' }}>
              <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sub)' }}>{t('billing.or', 'or pay by card')}</div>
              <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
            </div>
          </React.Fragment>
        )}

        <div id="crew-card-fields" style={{ minHeight: 90, marginBottom: 12 }} />

        {mode === 'create' && (
          <div style={{ fontSize: 13, color: 'var(--sub)', marginBottom: 12, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{t('billing.subtotal', 'Subtotal')}</span><span>{fmtUSD(baseCents)}</span></div>
            {rate > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{t('billing.waTax', 'WA sales tax ({pct}%)', { pct: (rate * 100).toFixed(2) })}</span><span>{fmtUSD(taxCents)}</span></div>}
            {taxBusy && <div style={{ fontSize: 12 }}>{t('billing.lookingUp', 'Looking up WA rate…')}</div>}
            {waUnresolved && <div style={{ fontSize: 12, color: 'var(--accent)' }}>{t('billing.addrUnverified', "Couldn't verify that WA address — check street/ZIP.")}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, color: 'var(--ink)', marginTop: 4 }}>
              <span>{cycle === 'annual' ? t('billing.totalYear', 'Total / year') : t('billing.totalMonth', 'Total / month')}</span><span>{fmtUSD(totalCents)}</span>
            </div>
          </div>
        )}

        {err && <div style={{ color: 'var(--primary)', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <button onClick={pay} disabled={paying} style={{
          width: '100%', padding: '13px', cursor: paying ? 'default' : 'pointer', borderRadius: 'var(--radius-sm)', border: 'none',
          background: 'var(--primary)', color: 'var(--primary-ink)', fontWeight: 700, fontSize: 15, opacity: paying ? 0.6 : 1,
        }}>{paying ? t('billing.processing', 'Processing…') : (mode === 'update' ? t('billing.updateCard', 'Update card') : t('billing.payActivate', 'Pay {amount} & activate', { amount: fmtUSD(totalCents) }))}</button>
        <button onClick={onClose} style={{ width: '100%', marginTop: 8, padding: 10, border: 'none', background: 'none', color: 'var(--sub)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{t('common.cancel', 'Cancel')}</button>
      </div>
    </div>
  );
}

function BillingScreen() {
  const wid = window.crew && window.crew.state.activeWorkplaceId;
  const [status, setStatus] = React.useState(null);
  const [email, setEmail] = React.useState('');
  const [modal, setModal] = React.useState(null); // { cycle, mode } | null
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!wid) return;
    try { setStatus(await window.crew.billing.subscription.getStatus(wid)); }
    catch (e) { console.warn('[billing] status failed', e && e.message); setStatus({ status: 'FREE', tier: 'free' }); }
  }, [wid]);

  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { window.crew.auth.currentClaims().then((c) => { if (c && c.email) setEmail(c.email); }).catch(() => {}); }, []);

  const onDone = async () => { setModal(null); await load(); };

  const cancel = async () => {
    if (!window.confirm(t('billing.cancelConfirm', 'Cancel Multilingual? Translation stays on until the end of the paid period.'))) return;
    setBusy(true);
    try { await window.crew.billing.subscription.cancelSubscription(wid); await load(); }
    catch (e) { console.warn(e); } finally { setBusy(false); }
  };

  const active = status && (status.status === 'ACTIVE' || status.status === 'CANCELING' || status.tier === 'multilingual');

  return (
    <KShell tab="you">
      <KHeader back title={t('billing.title', 'Multilingual')} />
      <div style={{ padding: '12px 16px' }}>
        {status == null && <div style={{ padding: 16, color: 'var(--on-dim)' }}>…</div>}

        {status != null && !active && (
          <React.Fragment>
            <PlanCard title={t('billing.freeTitle', 'Free')} price={t('billing.free', 'Free (English only)')}>
              {t('billing.freeDesc', 'Board, discussions & schedule in one language. No translation.')}
            </PlanCard>
            <PlanCard title={t('billing.title', 'Multilingual')} price={t('billing.monthly', '$5 / month')} highlight action={t('billing.chooseMonthly', 'Choose monthly')} onAction={() => setModal({ cycle: 'monthly', mode: 'create' })}>
              {t('billing.multilingualDesc', 'Everyone reads & writes in their own language — auto-translated board & discussions.')}
            </PlanCard>
            <PlanCard title={t('billing.annualTitle', 'Multilingual — annual')} price={t('billing.annual', '$50 / year')} action={t('billing.chooseAnnual', 'Choose annual')} onAction={() => setModal({ cycle: 'annual', mode: 'create' })}>
              {t('billing.annualDesc', 'Same as monthly, ~2 months free.')}
            </PlanCard>
          </React.Fragment>
        )}

        {status != null && active && (() => {
            const pastDue = status.status === 'PAST_DUE';
            const canceling = status.status === 'CANCELING';
            const badge = pastDue ? { t: t('billing.statusPastDue', 'PAST DUE'), bg: 'var(--accent-soft)', fg: 'var(--accent)' }
              : canceling ? { t: t('billing.statusCanceling', 'CANCELING'), bg: 'var(--surface-2)', fg: 'var(--sub)' }
              : { t: t('billing.statusActive', 'ACTIVE'), bg: 'var(--ok-soft)', fg: 'var(--ok)' };
            const dateLabel = status.currentPeriodEnd ? new Date(status.currentPeriodEnd).toLocaleDateString() : null;
            return (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 18, color: 'var(--ink)' }}>{t('billing.title', 'Multilingual')}</span>
                  <span style={{ fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 999, background: badge.bg, color: badge.fg }}>{badge.t}</span>
                </div>
                {(() => {
                  const baseCents = status.cycle === 'annual' ? 5000 : 500;
                  const rate = Number(status.taxRate) || 0;
                  const totalCents = baseCents + Math.round(baseCents * rate);
                  const base = status.cycle === 'annual' ? t('billing.annual', '$50 / year') : t('billing.monthly', '$5 / month');
                  const priceLabel = rate > 0 ? `${fmtUSD(totalCents)} ${t('billing.inclTax', '(incl. WA tax)')}` : base;
                  const dateText = dateLabel ? ` · ${canceling ? t('billing.accessUntil', 'access until {date}', { date: dateLabel }) : t('billing.renews', 'renews {date}', { date: dateLabel })}` : '';
                  return (
                    <div style={{ fontSize: 13, color: 'var(--sub)', margin: '6px 0 14px' }}>
                      {priceLabel}{dateText}
                    </div>
                  );
                })()}
                {pastDue && (
                  <div style={{ fontSize: 13, color: 'var(--accent)', marginBottom: 12 }}>
                    {t('billing.pastDueMsg', 'Last payment failed — update your card to keep translation on.')}
                  </div>
                )}
                <button onClick={() => setModal({ cycle: status.cycle || 'monthly', mode: 'update' })} style={secondaryBtn}>{t('billing.updateCard', 'Update card')}</button>
                {!canceling && <button onClick={cancel} disabled={busy} style={{ ...secondaryBtn, color: 'var(--primary)' }}>{t('billing.cancel', 'Cancel subscription')}</button>}
              </div>
            );
          })()}
      </div>

      {modal && <CardModal wid={wid} cycle={modal.cycle} mode={modal.mode} email={email} onClose={() => setModal(null)} onDone={onDone} />}
    </KShell>
  );
}

const secondaryBtn = {
  width: '100%', padding: '11px 16px', marginTop: 8, cursor: 'pointer', borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)', fontWeight: 700, fontSize: 14,
};

Object.assign(window, { BillingScreen, PlanCard });
